import { apiGet, apiPost } from './utils';
import type { PagedResponse } from '../types';

export interface Email {
  key: string;
  uuid: string;
  identity: string;
  associated_type: string;
  associated_uuid: string;
  to_email: string;
  from_email: string;
  subject: string;
  content: string;
  status: 'requested' | 'sent' | 'delivered' | 'opened' | 'clicked';
  files: string[];
  date_created: string;
  date_updated: string;
  // Email threading headers (RFC 5322)
  message_id?: string;
  in_reply_to?: string;
  references?: string;
}

export interface EmailHistory {
  key: string;
  uuid: string;
  email: string;
  addr: string;
  event: string;
  timestamp: number;
  date_created: string;
}

export interface SendEmailParams {
  associated_type: string;
  associated_uuid: string;
  to_email: string;
  from_email: string;
  subject: string;
  content: string;
  files?: string; // Comma-separated file UUIDs
  reply_to_email_uuid?: string; // UUID of email we're replying to (for threading)
  [key: string]: unknown;
}

export const emailApi = {
  /**
   * Send an email
   */
  async send(params: SendEmailParams): Promise<Email> {
    return apiPost<Email>('/email/send', params);
  },

  /**
   * Resend a previously sent email
   */
  async resend(emailUuid: string): Promise<Email> {
    return apiPost<Email>('/email/resend', {
      email: emailUuid,
    });
  },

  /**
   * Get emails for an associated resource
   */
  async listByAssociation(
    associatedType: string,
    associatedUuid: string,
    cursor?: string
  ): Promise<PagedResponse<Email>> {
    return apiGet<PagedResponse<Email>>('/email/list/by-association', {
      associated_type: associatedType,
      associated_uuid: associatedUuid,
      cursor,
    });
  },

  /**
   * Get email delivery history (opened, clicked, bounced, etc.)
   */
  async getHistory(
    emailUuid: string,
    cursor?: string
  ): Promise<PagedResponse<EmailHistory>> {
    return apiGet<PagedResponse<EmailHistory>>('/email/history', {
      email: emailUuid,
      cursor,
    });
  },
};
